import { createRouter, createWebHistory } from 'vue-router'
import store from './store'
import { bootstrapAuth } from './api/authBootstrap'
import { confirmFormLeaveIfNeeded } from './utils/formLeaveGuardRegistry'
import { applyRoutePageMeta, DEFAULT_DESCRIPTION } from './utils/pageMeta'
import { setDocumentTitle } from './utils/documentTitle'

const auth = (path, name, component, meta = {}) => ({
  path,
  name,
  component: () => import(`./views/${component}`),
  meta: { requiresAuth: true, ...meta },
})

const landingPageMeta = {
  title: 'MyCo+ | Connect Your Company, Your Way',
  description: DEFAULT_DESCRIPTION,
  robots: 'index, follow',
  canonical: 'https://mycompanyplus.com/',
}

const noindexAuthMeta = (title) => ({
  title,
  description: DEFAULT_DESCRIPTION,
  robots: 'noindex, nofollow',
})

const routes = [
  {
    path: '/',
    name: 'landing-page',
    component: () => import('./views/LandingPage.vue'),
    meta: { pageMeta: landingPageMeta },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('./views/SignUpView.vue'),
    meta: { pageMeta: noindexAuthMeta('Sign Up | MyCo+') },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { pageMeta: noindexAuthMeta('Log in | MyCo+') },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('./views/ResetPasswordView.vue'),
    meta: { pageMeta: noindexAuthMeta('Reset password | MyCo+') },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('./views/PrivacyPolicyView.vue'),
    meta: {
      pageMeta: {
        title: 'Privacy Policy | MyCo+',
        description: 'Privacy Policy for MyCo+ (product of MyCompany+).',
        robots: 'index, follow',
        canonical: 'https://mycompanyplus.com/privacy',
      },
    },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('./views/TermsAndConditionsView.vue'),
    meta: {
      pageMeta: {
        title: 'Terms and Conditions | MyCo+',
        description: 'Terms and Conditions for MyCo+ (product of MyCompany+).',
        robots: 'index, follow',
        canonical: 'https://mycompanyplus.com/terms',
      },
    },
  },
  auth('/dashboard', 'dashboard', 'app/DashboardView.vue'),
  auth('/settings', 'settings', 'app/SettingsView.vue'),
  auth('/settings/reports/users', 'report-users', 'app/ReportUserView.vue'),
  auth('/settings/reports/inventory', 'report-inventory', 'app/ReportInventoryView.vue'),
  auth('/jobs', 'jobs', 'app/JobsView.vue'),
  auth('/forms', 'forms', 'app/FormsView.vue'),
  auth('/forms/submissions', 'form-submissions', 'app/FormSubmissionsView.vue'),
  auth('/forms/submissions/audits', 'submission-audits', 'app/SubmissionAuditsView.vue'),
  auth('/forms/submissions/audit', 'submission-audit', 'app/SubmissionAuditView.vue'),
  auth('/forms/notifications', 'form-notifications', 'app/FormNotificationSettingsView.vue'),
  auth('/forms/material-order', 'material-order', 'app/forms/MaterialOrderView.vue'),
  auth('/forms/timesheet', 'timesheet', 'app/forms/TimesheetFormView.vue'),
  auth('/forms/custom/build', 'custom-form-build', 'app/forms/CustomFormBuilderView.vue'),
  auth('/forms/custom/build/:id', 'custom-form-edit', 'app/forms/CustomFormBuilderView.vue'),
  auth('/forms/custom/:id', 'custom-form-fill', 'app/forms/CustomFormFillView.vue'),
  auth('/jobs/:id', 'job-detail', 'app/JobDetailView.vue'),
  {
    path: '/jobs/:id/edit',
    redirect: (to) => ({ path: `/jobs/${to.params.id}`, query: { edit: 'details' } }),
  },
  auth('/bulk-download', 'bulk-download', 'app/BulkDownloadView.vue'),
  auth('/reminders', 'reminders', 'app/RemindersView.vue'),
  auth('/notifications', 'notifications', 'app/NotificationsView.vue'),
  auth('/maintenance', 'maintenance', 'app/MaintenanceView.vue'),
  auth('/safety', 'safety', 'app/SafetyView.vue'),
  auth('/hr', 'hr', 'app/HrView.vue'),
  auth('/hours', 'hours', 'app/HoursView.vue'),
  auth('/hours/previous', 'hours-previous', 'app/HoursView.vue'),
  auth('/hours/custom', 'hours-custom', 'app/HoursView.vue'),
  auth('/hours/all-employees', 'hours-all-employees', 'app/AllEmployeeHoursView.vue'),
  auth('/inventory', 'inventory', 'app/InventoryView.vue'),
  auth('/inventory/by-job', 'inventory-by-job', 'app/JobInventoryByJobListView.vue'),
  auth('/inventory/by-job/:jobId', 'inventory-by-job-detail', 'app/JobInventoryByJobView.vue'),
  auth('/profile', 'profile', 'app/ProfileView.vue'),
  auth('/resource/:id', 'resource-view', 'app/ResourceView.vue'),
  auth('/help', 'help', 'app/HelpIndexView.vue'),
  auth('/help/admin', 'help-admin', 'app/HelpGuideView.vue'),
  auth('/help/regular', 'help-regular', 'app/HelpGuideView.vue'),
  auth('/help/updates', 'help-updates', 'app/HelpUpdatesView.vue'),
  auth('/platform/e2e', 'platform-e2e', 'platform/E2eCoverageView.vue', { requiresSuperAdmin: true }),
  auth('/platform/usage', 'platform-usage', 'platform/PlatformUsageView.vue', { requiresSuperAdmin: true }),
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('./views/NotFoundView.vue'),
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      const offset = to.hash === '#pricing' || to.hash === '#faq' ? 120 : 80
      return { el: to.hash, top: offset, behavior: 'smooth' }
    }
    return { top: 0, left: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  if (from.matched.length && from.fullPath !== to.fullPath && !confirmFormLeaveIfNeeded()) {
    next(false)
    return
  }

  // Legacy PHP bookmarks (e.g. /somepage.php, /forms/foo.php) → dashboard.
  // Guests are then sent to login via the requiresAuth check below.
  if (/\.php/i.test(to.path)) {
    next({ path: '/dashboard', replace: true })
    return
  }

  await bootstrapAuth()

  if (to.meta.requiresAuth && !store.state.user) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  if (to.meta.requiresSuperAdmin && !store.state.user?.isSuperAdmin) {
    next({ path: '/dashboard' })
    return
  }

  if ((to.name === 'login' || to.name === 'signup' || to.name === 'reset-password') && store.state.user) {
    next({ path: typeof to.query.redirect === 'string' ? to.query.redirect : '/dashboard' })
    return
  }

  next()
})

router.afterEach((to) => {
  applyRoutePageMeta(to)
  if (!to.meta.pageMeta && store.state.user) {
    setDocumentTitle(store.state.company?.name)
  }
})

export default router
